import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { STATUSES, ASSET_ICONS, ASSET_LABELS } from '../utils';
import EmployeePicker, { employeeName } from './EmployeePicker';

export default function TransferModal({ asset, onClose, onSuccess }) {
  const { withAuth } = useAuth();
  const [locations, setLocations] = useState([]);
  const [toEmployeeId, setToEmployeeId] = useState(asset.assigned_to_employee_id || '');
  const [toLocationId, setToLocationId] = useState(asset.current_location_id || '');
  const [toStatus, setToStatus] = useState(asset.status || 'active');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [selectedEmpName, setSelectedEmpName] = useState(asset.employee_name || '');

  // Locations for the "Move to Location" select.
  useEffect(() => {
    setLoading(true);
    withAuth(async () => {
      const locRes = await api.getLocations();
      setLocations(locRes.data || []);
    })
      .catch(() => setError('Failed to load locations'))
      .finally(() => setLoading(false));
  }, [withAuth]);

  function handleSelectEmployee(emp) {
    setToEmployeeId(emp?.id || '');
    setSelectedEmpName(employeeName(emp));
    // `emp.location_id` was checked here and is never present — GET
    // /api/employees selects `l.name AS home_location_name` and no location id
    // at all, so this auto-fill had silently never fired. Match on the name
    // against the locations already loaded for the select below.
    if (emp) {
      const home = locations.find(l => l.name === emp.home_location_name);
      if (home) setToLocationId(home.id);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError('');
    try {
      await withAuth(() => api.createTransfer({
        assetId: asset.id,
        toEmployeeId: toEmployeeId || null,
        toLocationId: toLocationId || null,
        toStatus,
        notes,
      }));
      onSuccess();
    } catch (err) {
      setError(err.message || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  }

  // No click-outside-to-close: a stray backdrop click would discard typed work.
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Transfer Asset</h3>

        <div style={{
          background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
          padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>{ASSET_ICONS[asset.asset_type]}</span>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{asset.display_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {asset.asset_tag} · {ASSET_LABELS[asset.asset_type]}
              {asset.employee_name && ` · Currently: ${asset.employee_name}`}
            </div>
          </div>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" /></div> : (
          <>
            {/* Employee searchable dropdown */}
            <div className="form-group">
              <label>Assign To Employee</label>
              <EmployeePicker
                value={toEmployeeId}
                selectedName={selectedEmpName}
                onChange={handleSelectEmployee}
              />
            </div>

            <div className="form-group">
              <label>Move to Location</label>
              <select value={toLocationId} onChange={e => setToLocationId(e.target.value)}>
                <option value="">— Select Location —</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.workday_id})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status After Transfer</label>
              <select value={toStatus} onChange={e => setToStatus(e.target.value)}>
                {STATUSES.filter(s => !['lost','stolen','retired'].includes(s.value)).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Reason for transfer…"
                style={{ minHeight: 64 }}
              />
            </div>
          </>
        )}

        {error && <div style={{ color: 'var(--red)', fontSize: 14, marginBottom: 12 }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting ? 'Transferring…' : 'Confirm Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}
